import type {
	OrderDetailResponseDto,
	OrderSetupDetailResponseDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useMarketRegionList } from "Market/Api/Query/useMarketRegionList";
import { transformOrderFieldsToObject } from "Order/Utilities/transformOrderFieldsToObject";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import { ContentCopy } from "@mui/icons-material";
import { Button } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { toast } from "sonner";
import { GeneralEditForm } from "../Components/Form/GeneralEditForm";

const orderResources = Resources.base.order.editForm;
const orderSharedResources = Resources.base.order.shared;

type GeneralEditCardWidgetProps = {
	order: OrderDetailResponseDto;
	orderSetup?: OrderSetupDetailResponseDto;
};

export function GeneralEditCardWidget({
	order,
	orderSetup,
}: GeneralEditCardWidgetProps) {
	const { t } = useResource();
	const { watch } = useFormContext();
	const { data: marketRegionList, isLoading: isLoadingMarketRegions } =
		useMarketRegionList(order.market?.id);

	const handleCopyOrder = async (
		values: Record<string, string | null | undefined | number>,
	) => {
		const formattedText = Object.entries(values)
			.map(([key, value]) => `${key}\t${value}`)
			.join("\n");

		await navigator.clipboard.writeText(formattedText);
		toast.success(t(orderSharedResources.copySuccess));
	};

	const orderDataToBeCopied = {
		marketRegion:
			marketRegionList?.find((region) => region.id === watch("marketRegionId"))
				?.name ?? "",
		...transformOrderFieldsToObject(watch("fields")),
	};

	return (
		<BaseCard
			title={t(orderResources.generalData)}
			action={
				<Button
					size="small"
					variant="contained"
					onClick={() => handleCopyOrder(orderDataToBeCopied)}
					sx={{ minWidth: 0 }}
				>
					<ContentCopy fontSize="small" />
				</Button>
			}
		>
			<GeneralEditForm
				orderSetup={orderSetup}
				marketRegionList={marketRegionList}
				isLoadingMarketRegions={isLoadingMarketRegions}
			/>
		</BaseCard>
	);
}
